import { useEffect, useState, useRef, useCallback } from 'react';
import { useLocation } from 'react-router';
import { SESSION_TIMEOUT_MINUTES, TOKEN_DATE_KEY } from './constants';

export const useWindowDimensions = () => {
  const getWindowDimensions = () => {
    const { innerWidth: width, innerHeight: height } = window;
    return {
      width,
      height
    };
  };

  const [windowDimensions, setWindowDimensions] = useState(
    getWindowDimensions()
  );

  useEffect(() => {
    const handleResize = () => {
      setWindowDimensions(getWindowDimensions());
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  return windowDimensions;
};

export const useQuery = () => {
  const location = useLocation();
  return new URLSearchParams(location.search);
}

/**
 * Hook para cerrar sesión automáticamente después de un período de inactividad.
 * Escucha eventos globales del documento y reinicia el timer en cada interacción.
 * @param logOut - Función de cierre de sesión que se ejecutará al expirar el tiempo
 * @param timeoutMinutes - Minutos de inactividad antes de cerrar sesión (por defecto SESSION_TIMEOUT_MINUTES)
 */
export const useAutoLogout = (logOut: (showNotification?: boolean) => void, timeoutMinutes: number = SESSION_TIMEOUT_MINUTES) => {
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  const resetTimer = useCallback(() => {
    // Limpiar timer existente
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    // Actualizar tiempo de última actividad en localStorage
    const token = localStorage.getItem('token');
    if (token) {
      localStorage.setItem(TOKEN_DATE_KEY, new Date().toString());
    }

    // Iniciar nuevo timer
    timeoutRef.current = setTimeout(() => {
      logOut(true);
    }, timeoutMinutes * 60 * 1000);
  }, [logOut, timeoutMinutes]);

  useEffect(() => {
    // Solo activar si hay un token (usuario logueado)
    const token = localStorage.getItem('token');
    if (!token) return;

    // Eventos que reinician el timer de inactividad
    const events = ['mousedown', 'mousemove', 'keydown', 'scroll', 'touchstart', 'click'];

    const handleActivity = () => {
      resetTimer();
    };

    // Agregar listeners a todos los eventos
    events.forEach(event => {
      document.addEventListener(event, handleActivity);
    });

    // Iniciar timer al montar el componente
    resetTimer();

    // Cleanup: remover listeners y limpiar timer
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      events.forEach(event => {
        document.removeEventListener(event, handleActivity);
      });
    };
  }, [resetTimer]);
};

